import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { GableTruss, HeaderTruss, LeanToTruss, TrussBase, TrussRequest } from '../../../types';
import GableTrussAccordion from './gable-truss-accordion';
import HeaderTrussAccordion from './header-truss-accordion';
import LeanToTrussAccordion from './lean-to-truss-accordion';

interface TrussSectionsContainerProps {
  gableTrusses: GableTruss[];
  headerTrusses: HeaderTruss[];
  leanToTrusses: LeanToTruss[];
  postSize: string;
  orderDate: string;
  baySpacing?: number;
  onAddGableTruss: () => void;
  onAddHeaderTruss: () => void;
  onAddLeanToTruss: () => void;
  onUpdateGableTruss: (index: number, trussRequest: TrussRequest) => void;
  onUpdateHeaderTruss: (index: number, trussRequest: TrussRequest) => void;
  onUpdateLeanToTruss: (index: number, trussRequest: TrussRequest) => void;
  onDeleteGableTruss: (index: number) => void;
  onDeleteHeaderTruss: (index: number) => void;
  onDeleteLeanToTruss: (index: number) => void;
  readOnly?: boolean;
  onConfirmTrussStateChange?: (trussId: number) => void;
}

const StateChangeFlag: React.FC<{
  truss: TrussBase;
  onConfirm?: (trussId: number) => void;
}> = ({ truss, onConfirm }) => {
  if (truss.stateChangeConfirmation !== false) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5 }}>
      <Chip
        icon={<WarningAmberIcon />}
        label="State change - confirmation required"
        color="warning"
        size="small"
        variant="outlined"
        sx={{ py: 0.5, pl: 0.5 }}
      />
      {onConfirm && truss.id > 0 && (
        <Button
          size="small"
          variant="text"
          color="warning"
          onClick={e => {
            e.stopPropagation();
            onConfirm(truss.id);
          }}
        >
          Confirm
        </Button>
      )}
    </Box>
  );
};

export const TrussSectionsContainer: React.FC<TrussSectionsContainerProps> = ({
  gableTrusses,
  headerTrusses,
  leanToTrusses,
  postSize,
  orderDate,
  baySpacing = 0,
  onAddGableTruss,
  onAddHeaderTruss,
  onAddLeanToTruss,
  onUpdateGableTruss,
  onUpdateHeaderTruss,
  onUpdateLeanToTruss,
  onDeleteGableTruss,
  onDeleteHeaderTruss,
  onDeleteLeanToTruss,
  readOnly = false,
  onConfirmTrussStateChange,
}) => {
  return (
    <Box>
      {/* Gable Trusses Section */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Gable Trusses
          </Typography>
          <IconButton size="small" color="primary" onClick={onAddGableTruss} disabled={readOnly}>
            <AddIcon />
          </IconButton>
        </Box>
        {/* Gable Truss Accordions */}
        {gableTrusses.map((truss, index) => (
          <Box key={truss.id || `new-gable-${index}`}>
            <StateChangeFlag truss={truss} onConfirm={onConfirmTrussStateChange} />
            <GableTrussAccordion
              truss={truss}
              postSize={postSize}
              orderDate={orderDate}
              onUpdate={
                readOnly ? () => {} : trussRequest => onUpdateGableTruss(index, trussRequest)
              }
              onDelete={readOnly ? () => {} : () => onDeleteGableTruss(index)}
              defaultExpanded={!readOnly && index === gableTrusses.length - 1 && truss.id === 0}
            />
          </Box>
        ))}
        {gableTrusses.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No gable trusses added. Click the + icon to add one.
          </Typography>
        )}
      </Paper>

      {/* Header Trusses Section */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Header Trusses
          </Typography>
          <Tooltip
            title={
              gableTrusses.length === 0 && leanToTrusses.length === 0
                ? 'Header truss cannot be added unless a gable or lean-to truss is added first'
                : 'Add Header Truss'
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={onAddHeaderTruss}
                disabled={readOnly || (gableTrusses.length === 0 && leanToTrusses.length === 0)}
              >
                <AddIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Header Truss Accordions */}
        {headerTrusses.map((truss, index) => (
          <Box key={truss.id || `new-header-${index}`}>
            <StateChangeFlag truss={truss} onConfirm={onConfirmTrussStateChange} />
            <HeaderTrussAccordion
              truss={truss}
              postSize={postSize}
              orderDate={orderDate}
              baySpacing={baySpacing}
              onUpdate={
                readOnly ? () => {} : trussRequest => onUpdateHeaderTruss(index, trussRequest)
              }
              onDelete={readOnly ? () => {} : () => onDeleteHeaderTruss(index)}
              defaultExpanded={!readOnly && index === headerTrusses.length - 1 && truss.id === 0}
            />
          </Box>
        ))}
        {headerTrusses.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No header trusses added. Click the + icon to add one.
          </Typography>
        )}
      </Paper>

      {/* Lean-To Trusses Section */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Lean-To Trusses
          </Typography>
          <IconButton size="small" color="primary" onClick={onAddLeanToTruss} disabled={readOnly}>
            <AddIcon />
          </IconButton>
        </Box>
        {/* Lean-To Truss Accordions */}
        {leanToTrusses.map((truss, index) => (
          <Box key={truss.id || `new-leanto-${index}`}>
            <StateChangeFlag truss={truss} onConfirm={onConfirmTrussStateChange} />
            <LeanToTrussAccordion
              truss={truss}
              postSize={postSize}
              orderDate={orderDate}
              onUpdate={
                readOnly ? () => {} : trussRequest => onUpdateLeanToTruss(index, trussRequest)
              }
              onDelete={readOnly ? () => {} : () => onDeleteLeanToTruss(index)}
              defaultExpanded={!readOnly && index === leanToTrusses.length - 1 && truss.id === 0}
            />
          </Box>
        ))}
        {leanToTrusses.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No lean-to trusses added. Click the + icon to add one.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
